import { Component, OnInit, Inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ILab } from '../../../../app/models/labs/lab';
import { LabService } from '../../../../app/services/lab-service';
import { OregonWellDataService } from '../../../../app/services/oregon-well-data.service';
import { AuthService } from '@shared/auth/auth.service';

/*
 * Edit Lab Dialog
 */
@Component({
  selector: 'app-edit-lab',
  templateUrl: 'edit-lab-dialog.html',
  styleUrls: ['./edit-lab-dialog.scss']
})
export class DialogEditLabComponent implements OnInit {
  buttonText: string = "";
  loading: boolean = false;
  processing: boolean = false;
  labDetailsId: number = 0;
  postErrorMessages: string = "";

  lab: ILab = {
    id: 0,
    orelap: '',
    labName: '',
    labAddress1: '',
    labAddress2: '',
    labCity: '',
    labState: '',
    labZip: '',
    labCounty: '',
    labEmail: '',
    labPhone: '',
    labContact: '',
    active: true,
    displayName: '',
    comments: '',
    mailingAddress1: '',
    mailingAddress2: '',
    mailingCity: '',
    mailingState: '',
    mailingZip: '',
    doingBusinessAs: ''
  }

  labForm: FormGroup = null;

  constructor(public labService: LabService, 
    public oregonWellDataService: OregonWellDataService,
    public authService: AuthService,
    private toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: number,
    private dialogRef: MatDialogRef<DialogEditLabComponent>) {
    this.loading = true;
    this.labDetailsId = this.data;
    this.loadLab();
  }

  ngOnInit(): void { 
    // ngOnInit
  }

  loadLab() {
    this.loading = true;

    if (this.labDetailsId > 0) {
      this.labService.getLab(this.labDetailsId).subscribe(
        result => {
          this.lab = result;
          this.buttonText = "Update";
          this.setFormValues();
          this.loading = false;
        },
        error => {
          this.toastr.error("Unable to load Organization");
          this.loading = false;
          this.dialogRef.close();
        }
      );
    }
    else {
      this.buttonText = "Add";
      this.setFormValues();
      this.loading = false;
    }
  }

  onSubmit() {
    if (!this.labForm.valid) {
      this.postErrorMessages = "Organization Name and Display Name are required";
      this.toastr.error("Please fix validation errors");
      return;
    }

    this.getFormValues();

    this.labService.saveLab(this.lab).subscribe(
      result => {
        this.loadLab();
        this.toastr.success("Organization updated");
        this.processing = false;
      },
      error => {
        this.processing = false;
        this.toastr.error("Organization did not Update " + error.message);
      },
      () => {
        this.dialogRef.close();
      }
    );
  }

  getFormValues(){
    this.lab.active = this.labForm.value.active == "true";
    this.lab.orelap = this.labForm.value.orelap;
    this.lab.labName = this.labForm.value.labName;
    this.lab.displayName = this.labForm.value.displayName;
    this.lab.labAddress1 = this.labForm.value.labAddress1;
    this.lab.labAddress2 = this.labForm.value.labAddress2;
    this.lab.labCity = this.labForm.value.labCity;
    this.lab.labState = this.labForm.value.labState;
    this.lab.labZip = this.labForm.value.labZip;
    this.lab.labCounty = this.labForm.value.labCounty;
    this.lab.labEmail = this.labForm.value.labEmail;
    this.lab.labPhone = this.labForm.value.labPhone;
    this.lab.labContact = this.labForm.value.labContact;
    this.lab.comments =  this.labForm.value.comments;
    this.lab.mailingAddress1 = this.labForm.value.mailingAddress1;
    this.lab.mailingAddress2 = this.labForm.value.mailingAddress2;
    this.lab.mailingCity = this.labForm.value.mailingCity;
    this.lab.mailingState = this.labForm.value.mailingState;
    this.lab.mailingZip = this.labForm.value.mailingZip;
    this.lab.doingBusinessAs = this.labForm.value.doingBusinessAs;
  }

  setFormValues() {
    this.labForm = new FormGroup({
      active: new FormControl('true'),
      orelap: new FormControl(''),
      labName: new FormControl('', Validators.required),
      displayName: new FormControl('', Validators.required),
      labAddress1: new FormControl(''),
      labAddress2: new FormControl(''),
      labCity: new FormControl(''),
      labState: new FormControl(''),
      labZip: new FormControl(''),
      labCounty: new FormControl(''),
      labEmail: new FormControl(''),
      labPhone: new FormControl(''),
      labContact: new FormControl(''),
      comments: new FormControl(''),
      mailingAddress1: new FormControl(''),
      mailingAddress2: new FormControl(''),
      mailingCity: new FormControl(''),
      mailingState: new FormControl(''),
      mailingZip: new FormControl(''),
      doingBusinessAs: new FormControl('')
    });

    if (this.labDetailsId > 0) {
      this.labForm.get('active').setValue(this.lab.active.toString());
      this.labForm.get('orelap').setValue(this.lab.orelap);
      this.labForm.get('labName').setValue(this.lab.labName);
      this.labForm.get('displayName').setValue(this.lab.displayName);
      this.labForm.get('labAddress1').setValue(this.lab.labAddress1);
      this.labForm.get('labAddress2').setValue(this.lab.labAddress2);
      this.labForm.get('labCity').setValue(this.lab.labCity);
      this.labForm.get('labState').setValue(this.lab.labState);
      this.labForm.get('labZip').setValue(this.lab.labZip);
      this.labForm.get('labCounty').setValue(this.lab.labCounty);
      this.labForm.get('labEmail').setValue(this.lab.labEmail);
      this.labForm.get('labPhone').setValue(this.lab.labPhone);
      this.labForm.get('labContact').setValue(this.lab.labContact);
      this.labForm.get('comments').setValue(this.lab.comments);
      this.labForm.get('mailingAddress1').setValue(this.lab.mailingAddress1);
      this.labForm.get('mailingAddress2').setValue(this.lab.mailingAddress2);
      this.labForm.get('mailingCity').setValue(this.lab.mailingCity);
      this.labForm.get('mailingState').setValue(this.lab.mailingState);
      this.labForm.get('mailingZip').setValue(this.lab.mailingZip);
      this.labForm.get('doingBusinessAs').setValue(this.lab.doingBusinessAs);
    }
  }
}